// External libraries
import React from 'react';
//@ts-expect-error untyped module
import { FieldError, FloatingLabelInput } from '@checkout/react-components';

// Internal modules
import {
  features,
  getContent,
  getLocale,
  logger,
  ONBOARD_OPTION_CHANGED_PAYLOAD,
} from '@/lib';
import {
  OptionalAccountFieldset,
  OptionalAccountOption,
} from './StyledComponents';
import { useStoreState } from '@/store';
import { RadioButton } from '../Common';
import { InfoIcon } from '../Common/Icons';
import { SignupFields } from '../SignupFields';
import { passwordStyleOverrides } from '@/styles';
import { useMountLogging } from '@/lib/reactHooks';
import { errorFieldStyleOverrides } from './styles';
//@ts-expect-error untyped file
import { changeHandler } from '../Form/handlers';
import { logBlurOrFocusEvent as focusHandler } from '../Form/utils';
import { GuestTermsAndLegalDisclosure } from '@/components/GuestTermsAndLegalDisclosure';
import type { ReactComponentsChangeHandler } from '@/types';
import { APP_MODE } from '@/types';
import { uncheckPreviousTermBox } from './utils';
import { CountryCodes } from '@/generated/graphql';
import { LazyKanaAndKanjiNames } from '../SignupFields/Components';
import { verifyAndResetFullNames } from '@/components/SignupFields/KanaAndKanjiNames/utils';

export const OnboardOptions = (): React.ReactElement => {
  useMountLogging('OnboardOptions');
  const displayError = useStoreState(
    ({ form }) => form.onboardOption.displayError,
  );
  const value = useStoreState(({ form }) => form.onboardOption.value);
  const isGuest = Boolean(value === APP_MODE.GUEST);
  const isSignup = Boolean(value === APP_MODE.SIGNUP);
  const { country } = getLocale();
  const isSignUpIneligible =
    features.isActive('shouldDisableSignup') ||
    (features.isActive('shouldDisableSignupForGibraltar') &&
      country === CountryCodes.Gi);

  const onChangeHandler: ReactComponentsChangeHandler = (
    e,
    newValue,
    oldValue,
    meta,
  ) => {
    // unchecking terms box before switching to the other onboard option
    uncheckPreviousTermBox(newValue);
    verifyAndResetFullNames();

    logger.fpti(
      'onboard_option_changed',
      ONBOARD_OPTION_CHANGED_PAYLOAD(newValue),
    );

    changeHandler(e, newValue, oldValue, meta);
  };

  return (
    <>
      {displayError && (
        <FieldError
          icon={<InfoIcon />}
          id="onboardOptionError"
          styleOverrides={errorFieldStyleOverrides}
        >
          {getContent('fieldErrors.optionalGuest.valueMissing')}
        </FieldError>
      )}

      <OptionalAccountOption data-testid="onboard-options">
        <RadioButton
          blurHandler={focusHandler}
          disabled={isSignUpIneligible}
          changeHandler={onChangeHandler}
          checked={isSignup}
          hasError={displayError}
          focusHandler={focusHandler}
          id="onboardOptionSignup"
          label={getContent('fieldLabels.optionalGuest.signup')}
          name="onboardOption"
          value="signup"
        />
        <OptionalAccountFieldset shouldAddMargin={isSignup}>
          {isSignup ? (
            <SignupFields />
          ) : (
            <FloatingLabelInput
              disabled={true}
              id="disabledPassword"
              inputStyleOverrides={passwordStyleOverrides}
              label={getContent('fieldLabels.password')}
              value=""
            />
          )}
        </OptionalAccountFieldset>
      </OptionalAccountOption>

      <OptionalAccountOption>
        <RadioButton
          blurHandler={focusHandler}
          changeHandler={onChangeHandler}
          checked={isGuest}
          focusHandler={focusHandler}
          hasError={displayError}
          id="onboardOptionGuest"
          label={getContent('fieldLabels.optionalGuest.guest')}
          name="onboardOption"
          value="guest"
        />
        <OptionalAccountFieldset
          shouldAddMargin={features.isActive(
            'shouldDisplayJpKanaAndKanjiNames',
          )}
        >
          {isGuest && (
            <>
              {features.isActive('shouldDisplayJpKanaAndKanjiNames') && (
                <LazyKanaAndKanjiNames />
              )}
              <GuestTermsAndLegalDisclosure />
            </>
          )}
        </OptionalAccountFieldset>
      </OptionalAccountOption>
    </>
  );
};
